import React, { Component } from "react";
import ReactGA from "react-ga";
import BannerImg from "../../assets/images/banner/cms.png";
import CMSFlow from "../../assets/images/cms_flow.gif";
import ProductHeritagePage from "../../components/product/product-heritage-page";

const page = {
  title: "Content Management System, Portal Platform & Enterprise CMS Heritage | Avani Cimcon",
  description:
    "Avani Cimcon's CMS heritage shows multilingual portal engineering, booking-engine integration, content operations and platform thinking for enterprise websites.",
  keywords:
    "Content Management System, CMS Lite, CMS Lite Software, CMS Social Networking, CMS enterprise, CMS BOX, cmsbox, content management system, box social networking solutions, white label social networking solution, out-of the box solution, network social solutions, enterprise software engineering, legacy modernization, AI enablement, travel portal, booking engine, portal platform",
  canonical: "https://www.avanicimcon.com/cms",
  ogImage: "https://www.avanicimcon.com/images/banner/cms.png",
  hero: {
    eyebrow: "Product Heritage",
    heading: "CMS",
    lead:
      "CMS demonstrates how Avani Cimcon engineered multilingual content platforms that could sit in front of booking engines, social features and enterprise workflows.",
    image: BannerImg,
    tags: ["Portal platform", "Multilingual content", "Booking integration", "DIY publishing"],
    panelKicker: "Content systems as enterprise infrastructure.",
    panelTitle: "A portal platform designed for self-service publishing and integration.",
    panelText:
      "The CMS story is about more than editing web pages. It shows how content, contact data and booking workflows can be managed as a reusable enterprise capability.",
    stats: [
      { value: "CMS", label: "Productized content platform" },
      { value: "Multilingual", label: "Portal publishing capability" },
      { value: "Booking", label: "Integrated travel workflow support" },
    ],
    primaryCta: { label: "Contact Us", href: "/contact-us" },
    secondaryCta: { label: "See IT Consulting Services", href: "/it-consulting-services" },
  },
  overview: {
    eyebrow: "Platform Overview",
    title: "A content system built to let enterprises publish, reuse and control information",
    lead:
      "The platform proves that Avani Cimcon understood content operations as part of enterprise engineering, not just website decoration.",
    paragraphs: [
      "Avani Cimcon's Content Management System is designed to let non-technical users maintain and manage web content quickly and cost-effectively.",
      "It works on the do-it-yourself model: users can create images, text, documents and web pages, store them for reuse and insert them without needing HTML knowledge or constant developer support.",
    ],
    callout: {
      title: "What the page should prove",
      text: "This page should read as evidence of platform thinking around content operations, not a simple product listing.",
      items: [
        "Self-service publishing",
        "Reusable content objects",
        "Enterprise portal operations",
      ],
    },
  },
  businessProblem: {
    eyebrow: "Business Problem Solved",
    title: "Enterprises needed a way to control content without constant development work",
    lead:
      "CMS was created for organizations that needed frequent updates, multilingual reach and better control of their web presence.",
    items: [
      {
        title: "Content volatility",
        text: "Travel and other enterprise sites had to change often, and manual development cycles were too slow.",
      },
      {
        title: "Non-technical ownership",
        text: "Business teams needed to publish and reuse content without deep technical help.",
      },
      {
        title: "Reusable assets",
        text: "The system had to support images, text and documents as shared assets, not one-off pages.",
      },
    ],
  },
  architecture: {
    eyebrow: "Enterprise Architecture",
    title: "A modular content platform that could front a booking engine or enterprise portal",
    lead:
      "The architecture section matters because the CMS was not just a website editor. It was designed as a front-end system for operational content and commerce flows.",
    paragraphs: [
      "CMS Enterprise was built as a customizable solution for small to large enterprises to launch websites quickly and cost efficiently.",
      "An ideal implementation was the front-end system to a booking engine, which makes the platform especially relevant to Avani Cimcon's travel and portal heritage.",
    ],
    callout: {
      title: "Architecture highlights",
      items: [
        "Portal front-end capability",
        "Reusable content storage",
        "Multilingual publishing support",
        "Integration with booking systems",
      ],
    },
  },
  capabilities: {
    eyebrow: "Key Platform Capabilities",
    title: "The platform offered multiple modes for different enterprise needs",
    lead:
      "Those modes show product maturity and practical delivery thinking.",
    items: [
      {
        title: "CMS Lite",
        text: "An out-of-the-box solution for small companies and professionals who wanted to update content themselves.",
      },
      {
        title: "CMS Social Networking",
        text: "A solution for blogs, broadcast, forums and other consumer-driven sharing workflows.",
      },
      {
        title: "CMS Enterprise",
        text: "A customizable solution that could support larger enterprises and front-end booking experiences.",
      },
      {
        title: "Search Friendly Publishing",
        text: "Pages could be created for SEO, reused across domains and extended with new business modules.",
      },
    ],
  },
  challenges: {
    eyebrow: "Engineering Challenges Solved",
    title: "Content operations had to stay flexible, multilingual and reusable",
    lead:
      "The product solved practical problems that enterprise web teams still face.",
    items: [
      {
        title: "Versioned content",
        text: "Teams needed a way to update, reuse and manage content without losing control.",
      },
      {
        title: "Multilingual publishing",
        text: "The system had to support multiple languages while remaining easy to manage.",
      },
      {
        title: "Portal consistency",
        text: "Content had to stay aligned across travel portals, destination sites and related applications.",
      },
    ],
  },
  scalability: {
    eyebrow: "Scalability & Operational Considerations",
    title: "The platform had to scale from small sites to enterprise portal operations",
    lead:
      "That scaling story is why the page belongs in the product heritage cluster.",
    paragraphs: [
      "CMS Lite covered smaller users, while CMS Enterprise supported more complex organizations. That tiering shows deliberate scaling of capability instead of a one-size-fits-all product pitch.",
      "It also shows operational awareness because continuous website updates, search visibility and reuse all have real business costs that the platform was designed to reduce.",
    ],
    callout: {
      title: "Operational lessons",
      items: [
        "Support content teams without adding complexity",
        "Keep portals maintainable over time",
        "Treat content as reusable enterprise data",
      ],
    },
  },
  media: {
    eyebrow: "Platform Imagery",
    title: "Screenshots and process visuals still matter here",
    lead:
      "The CMS flow diagram is part of the proof, so it stays visible as an engineering artifact.",
    items: [
      {
        src: CMSFlow,
        alt: "CMS flow diagram showing content management workflow",
        caption: "CMS workflow overview",
        text: "The diagram demonstrates how content creation, reuse and portal publishing fit into the platform story.",
      },
    ],
  },
  integration: {
    eyebrow: "Technology & Integration Experience",
    title: "The CMS connected content management to booking engines and other business modules",
    lead:
      "The integration story is what makes the page enterprise-grade rather than marketing-grade.",
    paragraphs: [
      "The system could integrate with booking engines and dynamic packaging, host domain-specific content with minor modifications across multiple domains and support contact management for marketing campaigns.",
      "That makes the CMS a useful proof page for productized engineering because it connects content, commerce and customer data in a single platform story.",
    ],
    callout: {
      title: "Integration capabilities",
      items: [
        "Booking engine integration",
        "Dynamic packaging support",
        "Domain-specific content reuse",
        "Marketing automation adjacency",
      ],
    },
  },
  benefits: {
    eyebrow: "How This Experience Benefits Our Clients",
    title: "CMS proves Avani Cimcon can engineer portal systems that scale with business needs",
    lead:
      "That is the real value to clients who need modern enterprise content operations.",
    items: [
      {
        title: "Portal design discipline",
        text: "We understand how content, search visibility and enterprise workflows interact.",
      },
      {
        title: "Reusable content architecture",
        text: "We know how to build systems that reduce duplicate work across channels and domains.",
      },
      {
        title: "Operational ownership",
        text: "We can give business teams more control without breaking technical governance.",
      },
      {
        title: "Integration readiness",
        text: "We know how to connect content systems to adjacent enterprise modules.",
      },
    ],
  },
  modernization: {
    eyebrow: "Legacy Modernization Opportunities",
    title: "The same content architecture can support modernization and platform consolidation",
    lead:
      "The CMS story maps naturally to modern portal redesign, content service extraction and experience modernization.",
    paragraphs: [
      "The do-it-yourself content model is still relevant when enterprises want to modernize portals without losing search equity or operational control.",
      "The platform also supports the wider idea of enterprise content services, where content is managed once and reused many times across channels and products.",
    ],
    callout: {
      title: "Modernization priorities",
      items: [
        "Preserve SEO-oriented content structures",
        "Reduce dependence on manual web publishing",
        "Connect content to portals and commerce systems",
        "Keep multilingual and domain-specific reuse intact",
      ],
    },
  },
  ai: {
    eyebrow: "AI Enablement Opportunities",
    title: "AI can improve content operations after the platform is already organized",
    lead:
      "AI belongs after the content architecture story because the real opportunity is in content operations, not in hype.",
    items: [
      {
        title: "Content generation support",
        text: "Use AI to draft, summarize and localize content while keeping human control.",
      },
      {
        title: "Metadata assistance",
        text: "Use AI to support tagging, categorization and reuse of portal assets.",
      },
      {
        title: "Search and discovery",
        text: "Use AI to help users find the right content faster across large portal estates.",
      },
    ],
  },
  relatedIndustries: {
    eyebrow: "Related Industries",
    title: "CMS has strong relevance where content and commerce meet",
    lead:
      "These links preserve the original industry authority and strengthen the platform story.",
    items: [
      { label: "Travel & Hospitality", href: "/travel-hospitality", text: "Portals, booking and content operations." },
      { label: "Insurance", href: "/insurance", text: "Document control and self-service content." },
      { label: "Legal", href: "/legal", text: "Document-heavy workflows and portal access." },
      { label: "Healthcare", href: "/healthcare", text: "Patient and provider information portals." },
    ],
  },
  relatedLegacyServices: {
    eyebrow: "Related Legacy Services",
    title: "The CMS story connects back to the wider enterprise engineering stack",
    lead:
      "These pages show that the platform was part of a broader delivery ecosystem.",
    items: [
      { label: "DXchange", href: "/dxchange", text: "Integration and service exposure." },
      { label: "IT Consulting Services", href: "/it-consulting-services", text: "Modernization and platform strategy." },
      { label: "BizRuleEngine", href: "/biz-rule-engine", text: "Rules-driven workflow behavior." },
      { label: "More Products", href: "/more-products", text: "Portfolio-level proof and cross-linking." },
    ],
  },
  why: {
    eyebrow: "Why Avani Cimcon",
    title: "CMS shows that Avani Cimcon could build platforms around content, commerce and control",
    lead:
      "That is valuable proof for enterprise buyers who need more than a brochure website vendor.",
    items: [
      {
        title: "Platform thinking",
        text: "The system was designed to be reused and extended, not just deployed once.",
      },
      {
        title: "Operational utility",
        text: "It addressed a real business need around content ownership and publishing speed.",
      },
      {
        title: "Technical depth",
        text: "Integration with booking engines and marketing workflows demonstrates breadth.",
      },
      {
        title: "Heritage value",
        text: "The page preserves longstanding keywords while giving them an enterprise frame.",
      },
    ],
  },
  cta: {
    eyebrow: "Talk to Us",
    title: "Use this content-platform experience to improve your own digital operations",
    text:
      "If your enterprise needs better content control, portal architecture or integration with business systems, we can help.",
    primary: { label: "Contact Us", href: "/contact-us" },
    secondary: { label: "Explore Enterprise AI", href: "/enterprise-ai" },
  },
};

class CMS extends Component {
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    return <ProductHeritagePage {...page} />;
  }
}

export default CMS;
